import { Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DatePipe } from '@angular/common';
import { MatSort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { MatSelect } from '@angular/material/select';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { fromEvent, merge, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, tap } from 'rxjs/operators';

import { AppState } from '../../../../../core/reducers';
import { QueryParamsModel } from '../../../../../core/_base/crud';
import { ContactRelationshipsService } from '../../../../../core/lists';
import { StringUtilsService } from '../../../../../core/services/string-utils.service';
import { GeneralAutocompleteComponent } from '../../../../partials/layout/general-autocomplete/general-autocomplete.component';

import { Category, Document, Entity } from '../../models';
import { AccessHistoryType, AccessHistoryUserType, EntityType } from '../../enums';
import { DocumentService } from '../../services';
import { RequestAccessHistoryPage } from '../../actions';
import { AccessHistoryDataSource } from '../../data-sources';

@Component({
  selector: 'kt-access-history-list',
  templateUrl: './access-history-list.component.html',
  styleUrls: ['./access-history-list.component.scss'],
})
export class AccessHistoryListComponent implements OnInit, OnDestroy {
  private subscriptions = new Subscription();
  dataSource: AccessHistoryDataSource;
  AccessTypes = Object.values(AccessHistoryType);
  UserTypes = Object.values(AccessHistoryUserType);
  displayedCols = [
    'id',
    'document_name',
    'document_category_name',
    'entities',
    'interactionType',
    'downloadReason',
    'accessTime',
    'userType',
    'accessedBy',
    'actions',
  ];
  searchBarCols = [
    'searchId',
    'searchDocument',
    'searchCategory',
    'searchEntity',
    'searchAccessType',
    'searchDownloadReason',
    'searchAccessTime',
    'searchUserType',
    'searchAccessedBy',
    'searchActions',
  ];
  showSearchBar = false;
  getDocumentAutocomplete = this.documentService.findDocuments.bind(this.documentService, {});
  getCategoryAutocomplete = this.documentService.findCategories.bind(this.documentService, {});
  getEntityAutocomplete;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort: MatSort;
  @ViewChild('searchInput', { static: true }) searchInput: ElementRef;
  @ViewChild('searchIdInput') searchIdInput: ElementRef;
  @ViewChild('searchDocumentAutocomplete') searchDocumentAutocomplete: GeneralAutocompleteComponent;
  @ViewChild('searchCategoryAutocomplete') searchCategoryAutocomplete: GeneralAutocompleteComponent;
  @ViewChild('searchEntityAutocomplete') searchEntityAutocomplete: GeneralAutocompleteComponent;
  @ViewChild('searchAccessTypeSelect') searchAccessTypeSelect: MatSelect;
  @ViewChild('searchAccessTimeInput') searchAccessTimeInput: ElementRef;
  @ViewChild('searchUserTypeSelect') searchUserTypeSelect: MatSelect;
  @ViewChild('searchAccessedByInput') searchAccessedByInput: ElementRef;

  constructor(
    @Inject(MAT_DIALOG_DATA) public document: Document,
    public stringUtils: StringUtilsService,
    private dialogRef: MatDialogRef<AccessHistoryListComponent>,
    private datePipe: DatePipe,
    private store: Store<AppState>,
    private documentService: DocumentService,
    private relationshipsService: ContactRelationshipsService,
  ) {}

  ngOnInit(): void {
    this.dataSource = new AccessHistoryDataSource(this.store, this.paginator);
    this.getEntityAutocomplete = this.relationshipsService.findEntities.bind(
      this.relationshipsService,
      {
        entityTypes: this.document.id
          ? this.document.entities.map((entity) => entity.entity_type)
          : Object.values(EntityType),
      },
    );

    this.subscriptions.add(this.dataSource.entitySubject.subscribe());

    this.subscriptions.add(
      this.sort.sortChange.subscribe((res) => {
        this.paginator.pageIndex = 0;

        if (!res.direction) {
          this.sort.direction = '';
          this.sort.active = '';
        }
      }),
    );

    this.subscriptions.add(
      merge(this.sort.sortChange, this.paginator.page)
        .pipe(tap(() => this.loadAccessHistoryList()))
        .subscribe(),
    );

    this.subscriptions.add(
      fromEvent(this.searchInput.nativeElement, 'keyup')
        .pipe(
          debounceTime(500),
          distinctUntilChanged(),
          tap(() => this.reloadAccessHistoryList()),
        )
        .subscribe(),
    );

    setTimeout(() => this.loadAccessHistoryList());
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  getFilterConfig() {
    const filter: any = {};

    filter.documentId = this.document.id || this.searchDocumentAutocomplete?.value?.id;

    if (this.showSearchBar) {
      filter.id = this.searchIdInput?.nativeElement?.value;
      filter.categoryId = this.searchCategoryAutocomplete?.value?.id;
      filter.entityId = this.searchEntityAutocomplete?.value?.id;
      filter.accessType = this.searchAccessTypeSelect?.value;
      filter.accessTime = this.datePipe.transform(
        this.searchAccessTimeInput?.nativeElement?.value,
        'yyyy-MM-dd',
      );
      filter.userType = this.searchUserTypeSelect?.value;
      filter.accessedBy = this.searchAccessedByInput?.nativeElement?.value;
    } else {
      filter.searchTerm = this.searchInput.nativeElement.value || '';
    }

    return filter;
  }

  loadAccessHistoryList() {
    const query = new QueryParamsModel(
      this.getFilterConfig(),
      this.sort.direction || 'desc',
      this.sort.active || 'id',
      this.paginator.pageIndex,
      this.paginator.pageSize,
    );

    this.store.dispatch(new RequestAccessHistoryPage({ query }));
  }

  reloadAccessHistoryList(): void {
    this.paginator.pageIndex = 0;

    this.loadAccessHistoryList();
  }

  resetSearchBar(event?: Event): void {
    event?.stopPropagation();

    this.searchIdInput.nativeElement.value = '';
    this.searchDocumentAutocomplete?.setValue(null);
    this.searchDocumentAutocomplete?.updateValueAndValidity();
    this.searchCategoryAutocomplete.setValue(null);
    this.searchCategoryAutocomplete.updateValueAndValidity();
    this.searchEntityAutocomplete.setValue(null);
    this.searchEntityAutocomplete.updateValueAndValidity();
    this.searchAccessTypeSelect.value = '';
    this.searchAccessTimeInput.nativeElement.value = '';
    this.searchUserTypeSelect.value = '';
    this.searchAccessedByInput.nativeElement.value = '';
  }

  resetInput(event: Event, inputEl: HTMLInputElement, reload = false): void {
    event.stopPropagation();

    inputEl.value = '';

    if (reload) this.reloadAccessHistoryList();
  }

  toggleSearchBar(event: Event): void {
    event.stopPropagation();

    this.resetSearchBar();
    this.showSearchBar = !this.showSearchBar;

    if (this.showSearchBar) {
      this.searchInput.nativeElement.value = '';
      this.searchInput.nativeElement.disabled = true;
      setTimeout(() => this.searchIdInput.nativeElement.focus());
    } else {
      this.searchInput.nativeElement.disabled = false;
      this.searchInput.nativeElement.focus();
    }

    this.reloadAccessHistoryList();
  }

  handleSearchByField(event: Event): void {
    event.stopPropagation();

    this.reloadAccessHistoryList();
  }

  formatDocumentOption(document: Document): string {
    return document?.name || '';
  }

  formatCategoryOption(category: Category): string {
    return category?.name || '';
  }

  formatEntityOption(entity: Entity): string {
    return entity?.id
      ? `${
          entity.name || `${entity.first_name || ''} ${entity.last_name || ''}`
        }<small class="text-muted ml-2">(${entity.entity_type || ''})</small>`.trim()
      : '';
  }

  formatEntityValue(entity: Entity): string {
    return entity?.id
      ? `${entity.name || `${entity.first_name || ''} ${entity.last_name || ''}`}`.trim()
      : '';
  }

  getEntitiesString(entities: Entity[], startIdx = 0): string {
    return entities
      .slice(startIdx)
      .map((e) => this.formatEntityValue(e))
      .join(', ');
  }

  closeDialog(event: Event): void {
    event.stopPropagation();

    this.dialogRef.close();
  }
}
